import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Button, Card, Meter, Pagination, SegmentedControl, SortableHeader, Table, TableColumnsMenu, cn, pluralRu, useTableColumns, } from '@wit-support/ui';
import { Download } from 'lucide-react';
import { factHint, horizonRoas } from '../campaigns-logic.js';
import { downloadCsv, toCsv } from '../csv.js';
import { FACT_PER_DOLLAR, columnHint } from '../glossary.js';
import { intRu, money, num2, moneyWhole, sharePctText } from '../format.js';
import { markerKeyLabel } from '../marker-labels.js';
import { DEFAULT_MARKER_SORT, MARKER_COLUMN_DEFS, MARKER_COLUMN_LABEL, MARKER_SORT_KEYS, MIN_N, REST_ROW_VALUE_COLUMNS, bucketRows, markerCsvColumns, paginate, restRowSegments, sortValueRows, valueRows, } from '../portrait-logic.js';
import { legacyColumns, menuSort, paybackColumnsKey } from '../table-columns.js';
import { useTableUrl } from '../url-state.js';
import { verdictLabel } from '../verdict.js';
import { ChanceCell, CppCell } from './money-cells.js';
import { StripCell } from './strip-cell.js';
import { GroupVerdictBadge } from './verdict-badge.js';
const MUTED = 'text-[var(--color-text-muted)]';
/** Короткие шапки на экране (полные — в подсказке и в CSV): таблица помещается в 1440. */
const SHORT_LABEL = {
    fact: 'Вернулось',
    safe: 'Уверенно',
};
const ASC_FIRST = new Set(['value', 'cpp']);
function cell(key, r, theta, max) {
    const g = r.group;
    switch (key) {
        case 'value':
            return r.label;
        case 'share':
            return (_jsxs("div", { className: "flex min-w-[104px] items-center gap-1.5", children: [_jsx(Meter, { value: r.share * 100, className: "w-10", tone: "neutral" }), _jsxs("span", { className: "whitespace-nowrap", children: [sharePctText(r.sharePct, r.share), " \u00B7 ", intRu(g.purchases)] })] }));
        case 'spend':
            return moneyWhole(g.spendMinorFull);
        case 'purchases':
            return intRu(g.purchases);
        case 'cpp':
            return _jsx(CppCell, { cpp: g.cpp, breakeven: g.cppBreakevenMinor });
        case 'breakeven':
            return _jsx("span", { className: MUTED, children: money(g.cppBreakevenMinor) });
        case 'safe':
            return _jsx("span", { className: MUTED, children: money(g.cppSafeMinor) });
        case 'fact':
            return _jsx("span", { title: factHint(g), children: num2(g.factRoas) });
        case 'roas':
            return _jsx(StripCell, { group: g, theta: theta, max: max });
        case 'chance':
            return _jsx(ChanceCell, { p: g.answer.p, verdict: g.answer.verdict });
        case 'verdict':
            return _jsx(GroupVerdictBadge, { group: g });
        case 'h60':
            return num2(horizonRoas(g, '60'));
        case 'h90':
            return num2(horizonRoas(g, '90'));
    }
}
const left = (k) => k === 'value' || k === 'roas' || k === 'share' || k === 'verdict';
/** Значение строки «Остальные» в колонке со значением (`REST_ROW_VALUE_COLUMNS`). */
function restCell(key, rest) {
    switch (key) {
        case 'value':
            return (_jsxs(_Fragment, { children: ["\u041E\u0441\u0442\u0430\u043B\u044C\u043D\u044B\u0435 (", rest.count, " ", pluralRu(rest.count, ['группа', 'группы', 'групп']), ",", ' ', intRu(rest.purchases), " ", pluralRu(rest.purchases, ['покупка', 'покупки', 'покупок']), ")"] }));
        case 'share':
            return sharePctText(rest.sharePct, rest.share);
        case 'spend':
            return moneyWhole(rest.spendMinor);
        case 'purchases':
            return intRu(rest.purchases);
        default:
            return null;
    }
}
/**
 * «Один признак» (спека §6.4): таблица значений признака — доля покупок,
 * деньги, полоса, шанс, вывод; «Крупные группы ↔ Все значения»; сортировка,
 * пагинация, CSV.
 */
export function MarkerTable({ markerKey, values, theta, url, periodLabel, }) {
    const table = useTableUrl('m', MARKER_SORT_KEYS, DEFAULT_MARKER_SORT);
    const cols = useTableColumns(paybackColumnsKey('marker'), MARKER_COLUMN_DEFS, {
        legacy: legacyColumns('marker', MARKER_COLUMN_DEFS),
    });
    const all = url.search['groups'] === 'all';
    const rows = useMemo(() => valueRows(markerKey, values), [markerKey, values]);
    const { rows: shown, rest } = bucketRows(rows, all);
    const sorted = sortValueRows(shown, table.sort);
    const max = Math.max(2.2, ...rows.map((r) => r.group.answer.interval?.hi ?? 0));
    const paged = paginate(sorted, table.page, table.size);
    const pageRows = paged.rows;
    const lastPage = paged.page === paged.pageCount;
    const exportCsv = () => {
        const csv = toCsv(markerCsvColumns(cols.visibleIds, (g) => verdictLabel(g.answer.verdict)), sortValueRows(rows, table.sort));
        downloadCsv(`${markerKeyLabel(markerKey)} ${periodLabel}`, csv);
    };
    return (_jsxs("div", { className: "flex flex-col gap-2.5", children: [_jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx(SegmentedControl, { label: "\u041A\u0430\u043A\u0438\u0435 \u0437\u043D\u0430\u0447\u0435\u043D\u0438\u044F \u043F\u043E\u043A\u0430\u0437\u0430\u0442\u044C", value: all ? 'all' : 'big', onChange: (v) => url.set({ groups: v === 'all' ? 'all' : undefined, mpage: undefined }), options: [
                            { value: 'big', label: 'Крупные группы' },
                            { value: 'all', label: 'Все значения' },
                        ] }), _jsx("span", { className: cn('text-xs', MUTED), children: all
                            ? `все ${rows.length} значений`
                            : `значения от ${MIN_N} покупок, мелкие — одной строкой` }), _jsx("span", { className: "flex-1" }), _jsx(TableColumnsMenu, { columns: cols, sort: menuSort(table.sort, MARKER_COLUMN_DEFS) }), _jsxs(Button, { type: "button", size: "sm", variant: "secondary", className: "gap-1", onClick: exportCsv, children: [_jsx(Download, { "aria-hidden": true, className: "h-3.5 w-3.5" }), "CSV"] })] }), _jsx(Card, { children: _jsxs(Table, { density: "compact", stickyHeader: true, "data-testid": "marker-table", children: [_jsx(Table.Header, { children: _jsx(Table.Row, { children: cols.visible.map((col) => (_jsx(SortableHeader, { column: col, sortKey: col.id, sort: table.sort, onSortChange: table.setSort, align: left(col.id) ? 'left' : 'right', defaultDirection: ASC_FIRST.has(col.id) ? 'asc' : 'desc', className: "whitespace-nowrap", title: SHORT_LABEL[col.id] ? MARKER_COLUMN_LABEL[col.id] : undefined, hint: columnHint(col.id, theta, FACT_PER_DOLLAR), hintLabel: `Что значит «${MARKER_COLUMN_LABEL[col.id]}»`, children: SHORT_LABEL[col.id] ?? MARKER_COLUMN_LABEL[col.id] }, col.id))) }) }), _jsxs(Table.Body, { children: [pageRows.map((r) => (_jsx(Table.Row, { "data-testid": "value-row", className: cn(r.group.answer.verdict === null && MUTED), children: cols.visible.map((col) => (_jsx(Table.Cell, { column: col, className: cn('whitespace-nowrap tabular-nums', !left(col.id) && 'text-right'), children: cell(col.id, r, theta, max) }, col.id))) }, r.raw))), rest && lastPage ? (_jsx(Table.Row, { "data-testid": "rest-row", className: MUTED, children: restRowSegments(cols.visible, (id) => REST_ROW_VALUE_COLUMNS.has(id)).map((seg) => {
                                        if (seg.kind === 'value') {
                                            const col = cols.visible.find((c) => c.id === seg.id);
                                            return (_jsx(Table.Cell, { column: col, "data-testid": `rest-${seg.id}`, className: cn('tabular-nums', !left(seg.id) && 'text-right', seg.id !== 'value' && 'whitespace-nowrap'), children: restCell(seg.id, rest) }, seg.id));
                                        }
                                        const first = seg.ids[0];
                                        return (_jsx(Table.Cell, { column: cols.visible.find((c) => c.id === first), colSpan: seg.ids.length, "data-testid": seg.note ? 'rest-note' : undefined, children: seg.note
                                                ? 'мало покупок в каждой группе — прогноз не показываем; «Все значения» — каждая отдельно'
                                                : null }, first));
                                    }) })) : null] })] }) }), paged.total > table.size ? (_jsx(Pagination, { page: paged.page, pageSize: table.size, total: paged.total, onPageChange: table.setPage, onPageSizeChange: table.setSize })) : null] }));
}
//# sourceMappingURL=marker-table.js.map